import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, KeyRound, Loader2 } from 'lucide-react';

const SATISFIND_LOGO = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png';

/**
 * Set-password landing page.
 * Reached from invitation emails as /set-password?token=<uuid>.
 */
export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Invalid invitation link — token missing.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/users/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed to set password.');
      }
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)' }}
      data-testid="set-password-page"
    >
      <img
        src={SATISFIND_LOGO}
        alt="satisFIND"
        style={{ width: 160, height: 'auto' }}
        className="mb-8"
        data-testid="set-password-logo"
      />
      <Card className="w-full max-w-md border-[#E2E8F0] shadow-md">
        <CardHeader className="space-y-2">
          <div className="w-12 h-12 rounded-full bg-[#1A3E6F] flex items-center justify-center mx-auto">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A] text-center">
            Set Your Password
          </CardTitle>
          <p className="text-sm text-[#64748B] text-center">
            Choose a password to activate your satisFIND® account.
          </p>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4 py-4" data-testid="set-password-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-[#22C55E]" />
              <p className="text-sm text-[#0F172A]">
                Password set successfully. You can now log in.
              </p>
              <Link
                to="/auth"
                className="inline-flex items-center justify-center px-5 py-2 rounded-lg bg-[#1A3E6F] hover:bg-[#122c50] text-white text-sm font-semibold transition-colors"
                data-testid="set-password-login-link"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="set-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="set-password-confirm-input"
                />
              </div>
              {error && (
                <p
                  className="text-sm text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2"
                  data-testid="set-password-error"
                >
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1A3E6F] hover:bg-[#122c50] text-white font-semibold"
                data-testid="set-password-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting…</>
                ) : 'Set Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-[#94A3B8] mt-8">
        Powered by <span className="font-semibold">satisFIND®</span>
      </p>
    </div>
  );
}
